export enum CategoryEnum {
  InformationSecurityPolicy = 'Information Security Policy',
  AccessPolicy = 'Access Policy',
  AssetManagement = 'Asset Management',
  DeviceUsagePolicy = 'Device Usage Policy',
  PrivacyPolicy = 'Privacy Policy',
  DPOStatement = 'DPO Statement',
  DataCategory = 'Data Category',
  EnvironmentalPolicy = 'Environmental Policy',
  PaperPolicy = 'Paper Policy',
  BusinessContinuity = 'Business Continuity',
  DisasterRecovery = 'Disaster Recovery',
  Insurance = 'Insurance',
  HiringEmployees = 'Hiring Employees',
  EmployeeTermination = 'Employee Termination',
  IncidentResponse = 'Incident Response',
  AcceptableUse = 'Acceptable Use Policy',
  RemoteWork = 'Remote Work Policy',
  VendorManagement = 'Vendor Management',
  ChangeManagement = 'Change Management',
  PhysicalSecurity = 'Physical Security',
  SocialMedia = 'Social Media Policy',
  BYOD = 'Bring Your Own Device (BYOD)',
}

export enum AssetGroupEnum {
  SensitiveInfo = 'Sensitive Info',
  Hardware = 'Hardware',
  Endpoints = 'Endpoints',
  Servers = 'Servers',
  CriticalSystems = 'Critical Systems',
  MobileDevices = 'Mobile Devices',
  NetworkDevices = 'Network Devices',
  CloudResources = 'Cloud Resources',
  Applications = 'Applications',
}

export enum NetworkNatureEnum {
  Local = 'Local',
  CloudBased = 'Cloud-Based',
  Hybrid = 'Hybrid',
  VPN = 'VPN',
  // SDWAN = 'SD-WAN',
}

export enum DataScopeEnum {
  PaymentData = 'Payment Data',
  PHI = 'PHI',
  PII = 'PII',
  FinancialData = 'Financial Data',
  MilitaryData = 'Military Data',
  IntellectualProperty = 'Intellectual Property',
  CustomerData = 'Customer Data',
  EmployeeData = 'Employee Data',
  OperationalData = 'Operational Data',
}

export enum ApprovalFrequencyEnum {
  Annual = 'Annual',
  BiAnnual = 'Bi-Annual',
  Quarterly = 'Quarterly',
  Monthly = 'Monthly',
}

export enum SensitivityClassificationEnum {
  Public = 'Public',
  Internal = 'Internal',
  Confidential = 'Confidential',
  Restricted = 'Restricted',
  HighlyConfidential = 'Highly Confidential',
}
export enum optionType {
  AI = 'AI',
  Template = 'Template',
  Import = 'Import',
}
export enum frameworkType {
  RISK = 'RISK',
  COMPLIANCE = 'COMPLIANCE',
  CUSTOM = 'CUSTOM',
}
export enum summaryUserType {
  Approver = 'approver',
  Reviewer = 'reviewer',
  PolicyOwner = 'policyOwner',
}

export enum RelevantDepartmentsEnum {
  HR = 'HR',
  Compliance = 'Compliance',
  CustomerService = 'Customer Service',
  Finance = 'Finance',
  IT = 'IT',
  Legal = 'Legal',
  Marketing = 'Marketing',
  Operations = 'Operations',
  Sales = 'Sales',
  Security = 'Security',
  Support = 'Support',
}

export enum PolicyStatusEnum {
  DRAFT = 'DRAFT',
  REVIEW_IN_PROGRESS = 'REVIEW_IN_PROGRESS',
  REVIEW_ENDED = 'REVIEW_ENDED',
  NEEDS_REVISION = 'NEEDS_REVISION',
  APPROVAL_PENDING = 'APPROVAL_PENDING',
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
}

export enum PolicyRoleEnum {
  Creator = 'Creator',
  Reviewer = 'Reviewer',
  Approver = 'Approver',
}

export interface MyActionContext {
  policyRole: string;
  displayStatus: string;
  lastAction: string;
  nextStep: string;
  primaryAction: string | null; // button label or null if no action
  primaryActionKey: string | null; // action identifier for handler
}

/**
 * Full role+status → action context lookup.
 * Interpolates actual user names from the policy data.
 */
export function getMyActionContext(role: string, status: string, policy: any): MyActionContext {
  const reviewerName = getFirstName(policy?.reviewers);
  const approverName = getFirstName(policy?.approvers);
  const creatorName = policy?.creatorName || 'creator';

  const key = `${role}:${status}`;

  const matrix: Record<string, Omit<MyActionContext, 'policyRole'>> = {
    // Creator states
    'Creator:DRAFT': {
      displayStatus: 'Draft',
      lastAction: 'Policy created and saved as a draft.',
      nextStep: 'Finalize the draft and mark the policy as ready for review.',
      primaryAction: 'SEND TO APPROVER',
      primaryActionKey: 'sendToApprover',
    },
    'Creator:REVIEW_IN_PROGRESS': {
      displayStatus: 'In Review',
      lastAction: `Draft submitted for review by ${reviewerName}.`,
      nextStep: `Awaiting review by ${reviewerName}. You can skip review and send directly to approver.`,
      primaryAction: 'SEND TO APPROVER',
      primaryActionKey: 'sendToApprover',
    },
    'Creator:REVIEW_ENDED': {
      displayStatus: 'Review Ended',
      lastAction: `Review completed by ${reviewerName}.`,
      nextStep: 'Send the policy to the approver for final approval.',
      primaryAction: 'SEND TO APPROVER',
      primaryActionKey: 'sendToApprover',
    },
    'Creator:NEEDS_REVISION': {
      displayStatus: 'Needs Revision',
      lastAction: `Received comments from ${reviewerName} with revision requests.`,
      nextStep: `Address feedback from ${reviewerName} and update the policy.`,
      primaryAction: 'SEND TO APPROVER',
      primaryActionKey: 'sendToApprover',
    },
    'Creator:APPROVAL_PENDING': {
      displayStatus: 'Awaiting Approval',
      lastAction: 'Policy sent for approval.',
      nextStep: `No action, awaiting approval from ${approverName}.`,
      primaryAction: null,
      primaryActionKey: null,
    },
    'Creator:DENIED': {
      displayStatus: 'Denied – Revise Required',
      lastAction: `Denial received with required changes from ${approverName}.`,
      nextStep: `Revise policy based on ${approverName}'s feedback and resubmit.`,
      primaryAction: 'SEND TO APPROVER',
      primaryActionKey: 'sendToApprover',
    },
    'Creator:APPROVED': {
      displayStatus: 'Approved',
      lastAction: 'Policy was officially approved and recorded in the platform.',
      nextStep: policy?.isPublished ? 'Policy has been published.' : 'Publish the approved policy.',
      primaryAction: policy?.isPublished ? null : 'PUBLISH',
      primaryActionKey: policy?.isPublished ? null : 'publish',
    },

    // Reviewer states
    'Reviewer:REVIEW_IN_PROGRESS': {
      displayStatus: 'In Review',
      lastAction: 'Review in progress.',
      nextStep: 'Complete review and provide comments.',
      primaryAction: 'COMPLETE REVIEW',
      primaryActionKey: 'submitReview',
    },
    'Reviewer:REVIEW_ENDED': {
      displayStatus: 'Review Ended',
      lastAction: `Comments submitted to ${creatorName}.`,
      nextStep: `None, awaiting action from ${creatorName}.`,
      primaryAction: null,
      primaryActionKey: null,
    },

    // Approver states
    'Approver:APPROVAL_PENDING': {
      displayStatus: 'Awaiting Approval',
      lastAction: `Final policy received from ${creatorName} for approval.`,
      nextStep: 'Review policy and provide a decision or comments.',
      primaryAction: 'APPROVE',
      primaryActionKey: 'approvePolicy',
    },
    'Approver:APPROVED': {
      displayStatus: 'Approved',
      lastAction: `Policy approved. Approval logged by ${approverName}.`,
      nextStep: `Notify ${creatorName} and close the workflow.`,
      primaryAction: null,
      primaryActionKey: null,
    },
    'Approver:DENIED': {
      displayStatus: 'Denied',
      lastAction: `Policy denied. ${approverName} added comments explaining the decision and requested revisions.`,
      nextStep: `Return to ${creatorName} with required changes.`,
      primaryAction: null,
      primaryActionKey: null,
    },
  };

  const entry = matrix[key] || {
    displayStatus: status,
    lastAction: '-',
    nextStep: '-',
    primaryAction: null,
    primaryActionKey: null,
  };

  return { policyRole: role, ...entry };
}

function getFirstName(users: any): string {
  if (!users) {
    return 'assignee';
  }
  const arr = Array.isArray(users) ? users : [users];
  const first = arr[0];
  if (!first) {
    return 'assignee';
  }
  return first.name || first.email || 'assignee';
}
